import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.jonnycoe.com';
const SITE_NAME = 'Jonny Coe Photography';
const DEFAULT_OG_IMAGE = '/og-default.jpg';

/**
 * SEO — per-page metadata emitter.
 *
 * Pass a `content` object (the JSON files in src/content/*) or override
 * any individual prop. Emits title, description, canonical, Open Graph,
 * Twitter card, keywords, and any JSON-LD structured data provided.
 */
const SEO = ({
  content = {},
  title,
  description,
  path,
  ogImage,
  ogType = 'website',
  keywords,
  jsonLd,
  noindex = false,
}) => {
  const resolvedTitle = title || content.seoTitle || content.title || SITE_NAME;
  const resolvedDescription = description || content.seoDescription || '';
  const resolvedPath = path || content.path || '/';
  const canonical = `${SITE_URL}${resolvedPath === '/' ? '' : resolvedPath}`;
  const resolvedOgImage = ogImage || content.ogImage || DEFAULT_OG_IMAGE;
  const absoluteOgImage = resolvedOgImage.startsWith('http')
    ? resolvedOgImage
    : `${SITE_URL}${resolvedOgImage}`;
  const resolvedKeywords = keywords || content.keywords;

  const structuredData = Array.isArray(jsonLd)
    ? jsonLd
    : jsonLd
    ? [jsonLd]
    : [];

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      {resolvedDescription && (
        <meta name="description" content={resolvedDescription} />
      )}
      {resolvedKeywords && resolvedKeywords.length > 0 && (
        <meta name="keywords" content={resolvedKeywords.join(', ')} />
      )}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={resolvedTitle} />
      {resolvedDescription && (
        <meta property="og:description" content={resolvedDescription} />
      )}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:alt" content={resolvedTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      {resolvedDescription && (
        <meta name="twitter:description" content={resolvedDescription} />
      )}
      <meta name="twitter:image" content={absoluteOgImage} />

      {structuredData.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
